import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '../lib/socket';

export function useRealtimeUpdates(): { isConnected: boolean } {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);

  const handlePositionUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['positions'] });
  }, [queryClient]);

  const handleTradeUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['trades'] });
  }, [queryClient]);

  const handleSignalUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['signals'] });
  }, [queryClient]);

  const handleAlertUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['alerts'] });
  }, [queryClient]);

  const handleRiskUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['risk'] });
  }, [queryClient]);

  useEffect(() => {
    const socket = getSocket();

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('positions:update', handlePositionUpdate);
    socket.on('trades:update', handleTradeUpdate);
    socket.on('signals:update', handleSignalUpdate);
    socket.on('alerts:update', handleAlertUpdate);
    socket.on('risk:update', handleRiskUpdate);

    if (socket.connected) {
      setIsConnected(true);
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('positions:update', handlePositionUpdate);
      socket.off('trades:update', handleTradeUpdate);
      socket.off('signals:update', handleSignalUpdate);
      socket.off('alerts:update', handleAlertUpdate);
      socket.off('risk:update', handleRiskUpdate);
    };
  }, [handlePositionUpdate, handleTradeUpdate, handleSignalUpdate, handleAlertUpdate, handleRiskUpdate]);

  return { isConnected };
}
